// Kırılım boyut sözlüğü (spec §19 B1–B3, B7) — hazır boyut şablonları, özel boyut şemaları,
// kategoriye göre liste sıralaması ve benzer ad uyarısı. Saf, BE/FE ortak.

import { z } from "zod";
import { levenshtein } from "../utils/company-name.js";
import { normalizeMappingLabel } from "../utils/kpi-code.js";
import {
  KPI_BREAKDOWN_DIMENSIONS,
  KPI_BREAKDOWN_DIMENSION_LABELS,
  type KpiBreakdownDimension,
} from "./kpi.js";

/** Veri kataloğunun kök kategori kodları (FE `KPI_ROOT_ORDER` ile aynı sıra). */
export const KPI_ROOT_CODES = ["cevresel", "sosyal", "yonetisim", "ekonomik"] as const;
export type KpiRootCode = (typeof KPI_ROOT_CODES)[number];

/** Boyut kodu (slug) kalıbı — FE `DIMENSION_SLUG_RE` ile aynı. */
export const KPI_DIMENSION_SLUG_RE = /^[a-z][a-z0-9_]{0,39}$/;

export interface KpiBreakdownDimensionPreset {
  label: string;
  /** Ne zaman kullanılır — tek Türkçe cümle. */
  help: string;
  /** "Örnek üyeler: Kadın, Erkek" biçiminde ya da üyesiz boyutta yönerge. */
  example: string;
  /** Üye şablonu (sıralı); boş = kullanıcı yazar. */
  members: readonly string[];
  /** Uygun olduğu kök kategoriler; `"all"` = hepsi. */
  categories: readonly KpiRootCode[] | "all";
}

function preset(
  slug: KpiBreakdownDimension,
  help: string,
  members: readonly string[],
  categories: readonly KpiRootCode[] | "all",
  example?: string,
): KpiBreakdownDimensionPreset {
  return {
    label: KPI_BREAKDOWN_DIMENSION_LABELS[slug],
    help,
    example: example ?? `Örnek üyeler: ${members.join(", ")}`,
    members,
    categories,
  };
}

/** Hazır boyutlar — içerik spec §19 B1. `label` eski `_LABELS` ile birebir aynıdır. */
export const KPI_BREAKDOWN_DIMENSION_PRESETS: Record<
  KpiBreakdownDimension,
  KpiBreakdownDimensionPreset
> = {
  cinsiyet: preset(
    "cinsiyet",
    "Çalışan sayısını kadın/erkek olarak ayırmak için kullanın.",
    ["Kadın", "Erkek"],
    ["sosyal"],
  ),
  yaka: preset(
    "yaka",
    "Çalışanları mavi yaka/beyaz yaka olarak ayırmak için kullanın.",
    ["Mavi yaka", "Beyaz yaka"],
    ["sosyal"],
  ),
  yas_grubu: preset(
    "yas_grubu",
    "Çalışanları yaş aralıklarına göre ayırmak için kullanın.",
    ["30 yaş altı", "30–50", "50 yaş üstü"],
    ["sosyal"],
  ),
  calisma_turu: preset(
    "calisma_turu",
    "Çalışanları tam zamanlı/yarı zamanlı olarak ayırmak için kullanın.",
    ["Tam zamanlı", "Yarı zamanlı"],
    ["sosyal"],
  ),
  sozlesme_turu: preset(
    "sozlesme_turu",
    "Çalışanları iş sözleşmesinin süresine göre ayırmak için kullanın.",
    ["Belirsiz süreli", "Belirli süreli"],
    ["sosyal"],
  ),
  bolge: preset(
    "bolge",
    "Değeri ülke, şehir ya da bölge gibi coğrafi parçalara ayırmak için kullanın.",
    [],
    "all",
    "Üyeleri siz yazarsınız: örn. İstanbul, Ankara",
  ),
  kademe: preset(
    "kademe",
    "Çalışanları yönetim kademesine göre ayırmak için kullanın.",
    ["Üst yönetim", "Orta kademe", "Çalışan"],
    ["sosyal"],
  ),
  egitim: preset(
    "egitim",
    "Çalışanları eğitim düzeyine göre ayırmak için kullanın.",
    ["İlköğretim", "Lise", "Lisans", "Lisansüstü"],
    ["sosyal"],
  ),
  atik_turu: preset(
    "atik_turu",
    "Atık miktarını tehlikeli/tehlikesiz olarak ayırmak için kullanın.",
    ["Tehlikeli", "Tehlikesiz"],
    ["cevresel"],
  ),
  enerji_kaynagi: preset(
    "enerji_kaynagi",
    "Enerji tüketimini kaynağına göre ayırmak için kullanın.",
    ["Elektrik", "Doğal gaz", "Dizel", "Yenilenebilir"],
    ["cevresel"],
  ),
  kapsam: preset(
    "kapsam",
    "Emisyonu Kapsam 1, 2 ve 3 olarak ayırmak için kullanın.",
    ["Kapsam 1", "Kapsam 2", "Kapsam 3"],
    ["cevresel"],
  ),
  diger: preset(
    "diger",
    "Listede olmayan bir ölçüt için kendi boyutunuzu tanımlayın.",
    [],
    "all",
    "Üyeleri siz yazarsınız: örn. Tesis A, Tesis B",
  ),
};

// --- Özel (müşterinin tanımladığı) boyutlar ---

const Slug = z.string().regex(KPI_DIMENSION_SLUG_RE);
const Label = z.string().trim().min(1).max(80);
const Members = z.array(z.string().trim().min(1).max(80)).max(50);

export const KpiBreakdownCustomDimensionSchema = z.object({
  slug: Slug,
  label: Label,
  members: Members,
  categories: z.array(z.enum(KPI_ROOT_CODES)).default([]),
  createdAt: z.string(),
});
export type KpiBreakdownCustomDimension = z.infer<typeof KpiBreakdownCustomDimensionSchema>;

export const KpiBreakdownCustomDimensionCreateSchema = z.object({
  slug: Slug,
  label: Label,
  members: Members.optional(),
  categories: z.array(z.enum(KPI_ROOT_CODES)).optional(),
});
export type KpiBreakdownCustomDimensionCreate = z.infer<
  typeof KpiBreakdownCustomDimensionCreateSchema
>;

/** `PATCH /kpi/breakdown/dimensions/:slug` (K-B4) — slug değişmez; en az bir alan. */
export const KpiBreakdownCustomDimensionPatchSchema = z
  .object({
    label: Label.optional(),
    members: Members.optional(),
    categories: z.array(z.enum(KPI_ROOT_CODES)).optional(),
  })
  .refine((v) => v.label !== undefined || v.members !== undefined || v.categories !== undefined, {
    message: "En az bir alan gerekli",
  });
export type KpiBreakdownCustomDimensionPatch = z.infer<
  typeof KpiBreakdownCustomDimensionPatchSchema
>;

/** `GET /kpi/breakdown/dimensions` — hazır boyutlar (sıralı) + özel boyutlar. */
export const KpiBreakdownDimensionsResponseSchema = z.object({
  presets: z.array(z.enum(KPI_BREAKDOWN_DIMENSIONS)),
  custom: z.array(KpiBreakdownCustomDimensionSchema),
});
export type KpiBreakdownDimensionsResponse = z.infer<typeof KpiBreakdownDimensionsResponseSchema>;

/** Etiket tanıyıcısının (B7) okuduğu özel boyut kalıbı. */
export interface CustomDimensionPattern {
  slug: string;
  label: string;
  members: readonly string[];
}

// --- Liste bölümleri (B3) ---

export interface DimensionSections {
  suitable: KpiBreakdownDimension[];
  other: KpiBreakdownDimension[];
  custom: (CustomDimensionPattern & { categories?: readonly KpiRootCode[] })[];
}

/**
 * Boyut seçicinin üç bölümü: "Bu gösterge için uygun" (kök kategori eşleşen ya da `"all"`,
 * `diger` hariç) · "Diğer boyutlar" (kalanlar, `diger` en sonda) · "Sizin tanımladıklarınız"
 * (kategori eşleşenler üstte, her grup tr alfabetik). Kullanılmış boyut LİSTEDEN ÇIKMAZ —
 * pasifleştirme çağıranın işidir (bu yüzden `used` parametresi yok).
 */
export function dimensionSectionsFor(
  rootCode: KpiRootCode | null,
  custom: readonly (CustomDimensionPattern & { categories?: readonly KpiRootCode[] })[],
): DimensionSections {
  const suitable: KpiBreakdownDimension[] = [];
  const other: KpiBreakdownDimension[] = [];
  for (const slug of KPI_BREAKDOWN_DIMENSIONS) {
    if (slug === "diger") continue;
    const cats = KPI_BREAKDOWN_DIMENSION_PRESETS[slug].categories;
    const fits = rootCode !== null && (cats === "all" || cats.includes(rootCode));
    (fits ? suitable : other).push(slug);
  }
  other.push("diger");
  const byLabel = (a: { label: string }, b: { label: string }): number =>
    a.label.localeCompare(b.label, "tr");
  const matches = (c: { categories?: readonly KpiRootCode[] }): boolean =>
    rootCode !== null && (c.categories ?? []).includes(rootCode);
  const first = custom.filter(matches).sort(byLabel);
  const rest = custom.filter((c) => !matches(c)).sort(byLabel);
  return { suitable, other, custom: [...first, ...rest] };
}

// --- Benzer ad uyarısı ---

/** Karşılaştırma biçimi: TR katlanmış, "göre" soneki, çoğul ve yönelme eki atılmış. */
export function normalizeDimensionLabel(raw: string): string {
  return normalizeMappingLabel(raw)
    .replace(/\s+gore$/u, "")
    .replace(/(?<=[a-z]{3})l[ae]r(?:[ae])?$/u, "")
    .replace(/(?<=[a-z]{4})y?[ae]$/u, "")
    .trim();
}

/**
 * Var olan boyutlar arasında yazılan ada benzeyeni bulur: önce birebir slug/etiket, sonra
 * normalize etiketlerde Levenshtein ≤2 (uzunluk farkı ≤2). `all` sırasındaki ilk eşleşme döner;
 * çağıran hazır boyutları da `all`'a ekler. Engellemez — yalnız uyarı için.
 */
export function similarDimension(
  label: string,
  all: readonly { slug: string; label: string }[],
): { slug: string; label: string } | null {
  const n = normalizeDimensionLabel(label);
  if (n === "") return null;
  const raw = label.trim().toLowerCase();
  for (const d of all) {
    if (d.slug === raw || normalizeDimensionLabel(d.label) === n) return d;
  }
  for (const d of all) {
    const m = normalizeDimensionLabel(d.label);
    if (Math.abs(m.length - n.length) <= 2 && levenshtein(m, n) <= 2) return d;
  }
  return null;
}
